/**
 * YoySummaryMonthlyTable.jsx
 *
 * Summary tab for Monthly view — sums sales/forecasts/stock across all marketplaces.
 * Same structure as YoySummaryTable but operates on months instead of weeks.
 */

import React, { useMemo } from 'react';
import { MARKETPLACE_CONFIGS } from '../configs/marketplaceConfigs';

import {
  buildMonthlySalesIndex,
  getMonthlySalesTotalFromIndex,
  calculateMonthlyAverageGrowthFromIndex,
  calculateMonthlyForecastFromIndex,
  calculateMonthlyPercentageChange,
  calculateMonthlySeasonalityForecast,
  getCurrentMonthInfo,
  getHistoricalMonths,
  getForecastMonths,
} from '../utils/yoyMonthlyCalculations';

import { renderDaysSuppliedCell, getDueDateClass, calculateDueDate } from '../utils/yoyCalculations';

const ALL_MARKETPLACES = ['amazon', 'walmart', 'shopify', 'etsy'];

// Fixed left columns: product name + identifier + total stock + days supplied + due date + plan to produce
const FIXED_COL_COUNT = 6;

// Returns the correct identifier for a product in a given marketplace
// Amazon uses asin, all others use sku
const getIdentifierForMp = (product, mp) => {
  const field = MARKETPLACE_CONFIGS[mp]?.identifierField ?? 'asin';
  return product[field];
};

// Build flat-map key for monthly sales data
function mkKey(identifier, year, month) {
  return `${identifier}-${year}-${String(month).padStart(2, '0')}`;
}

const YoySummaryMonthlyTable = ({
  filteredProducts,
  identifierField,
  monthlySalesDataByMarketplace,
  monthlyPlanData,
  planToProduceData,
  visibleColumns,
  historicalMonthsCount = 6,
  forecastMonthsCount   = 6,
  showStockBalanceRow,
  seasonalityFlags,
  getSeasonalityCoefficient,
}) => {

  const currentMonthInfo = useMemo(() => getCurrentMonthInfo(), []);

  const historicalMonths = useMemo(
    () => getHistoricalMonths(currentMonthInfo.month, currentMonthInfo.year, historicalMonthsCount),
    [currentMonthInfo.month, currentMonthInfo.year, historicalMonthsCount]
  );

  const forecastMonths = useMemo(
    () => getForecastMonths(currentMonthInfo.month, currentMonthInfo.year, forecastMonthsCount),
    [currentMonthInfo.month, currentMonthInfo.year, forecastMonthsCount]
  );

  // ─── Pre-build sales indexes per marketplace per product ─────────────────
  // salesIndexes[mp][id] = buildMonthlySalesIndex result
  const salesIndexes = useMemo(() => {
    const result = {};
    for (const mp of ALL_MARKETPLACES) {
      result[mp] = {};
      const salesData = monthlySalesDataByMarketplace[mp] || {};
      for (const product of filteredProducts) {
        const id = getIdentifierForMp(product, mp);
        if (id) result[mp][id] = buildMonthlySalesIndex(id, salesData);
      }
    }
    return result;
  }, [filteredProducts, monthlySalesDataByMarketplace]);

  // ─── avgGrowth per product per marketplace ────────────────────────────────
  const avgGrowthMap = useMemo(() => {
    const map = {};
    for (const product of filteredProducts) {
      const primaryId = product[identifierField];
      map[primaryId] = {};
      for (const mp of ALL_MARKETPLACES) {
        const id = getIdentifierForMp(product, mp);
        if (!id) continue;
        const index = salesIndexes[mp]?.[id];
        map[primaryId][mp] = index
          ? calculateMonthlyAverageGrowthFromIndex(index, historicalMonths)
          : 0;
      }
    }
    return map;
  }, [filteredProducts, identifierField, salesIndexes, historicalMonths]);

  // ─── Helpers ───────────────────────────────────────────────────────────────

  /** Total stock = WH + AWD + all marketplace stock columns (deduped). */
  const getTotalStock = (product) => {
    let total = Number(product.whStock) || 0;
    total += Number(product.awdStock) || 0;
    const seen = new Set();
    for (const mp of ALL_MARKETPLACES) {
      for (const col of MARKETPLACE_CONFIGS[mp]?.stockColumns || []) {
        if (!seen.has(col.key)) {
          seen.add(col.key);
          total += Number(product[col.key]) || 0;
        }
      }
    }
    return total;
  };

  /**
   * Sum monthly sales across all marketplaces for a product/year/month.
   * Uses the correct identifier per marketplace (asin for Amazon, sku for others).
   */
  const sumMonthlySales = (product, year, month) => {
    let total = 0;
    let hasAny = false;
    for (const mp of ALL_MARKETPLACES) {
      const id = getIdentifierForMp(product, mp);
      if (!id) continue;
      const salesData = monthlySalesDataByMarketplace[mp] || {};
      const record = salesData[mkKey(id, year, month)];
      // prefer .value (median-adjusted), fall back to .units
      const v = record?.value !== undefined ? record.value : record?.units;
      if (v !== undefined && v !== null && v !== 'N/A') {
        total += Number(v);
        hasAny = true;
      }
    }
    return hasAny ? total : 'N/A';
  };

  /**
   * Forecast for a single marketplace/product/year/month.
   * Respects seasonality logic identical to YoyMonthlyView.
   */
  const getForecastForMp = (product, mp, year, month, avgGrowth) => {
    const id = getIdentifierForMp(product, mp);
    if (!id) return 'N/A';
    const config    = MARKETPLACE_CONFIGS[mp];
    const salesData = monthlySalesDataByMarketplace[mp] || {};
    const index     = salesIndexes[mp]?.[id];

    if (config?.hasSeasonality && product.niche_category_id && seasonalityFlags?.[product.asin]) {
      const seasonal = calculateMonthlySeasonalityForecast(
        id, year, month,
        product.niche_category_id,
        index || salesData,
        getSeasonalityCoefficient,
        currentMonthInfo.month,
        currentMonthInfo.year
      );
      if (seasonal !== 'N/A') return seasonal;
    }

    if (index) return calculateMonthlyForecastFromIndex(index, year, month, avgGrowth);
    return 'N/A';
  };

  /**
   * Sum forecasts across all marketplaces for a product/year/month.
   */
  const sumForecast = (product, year, month, avgGrowthByMp) => {
    let total = 0;
    let hasAny = false;
    for (const mp of ALL_MARKETPLACES) {
      const avgGrowth = avgGrowthByMp[mp] ?? 0;
      const v = getForecastForMp(product, mp, year, month, avgGrowth);
      if (v !== undefined && v !== null && v !== 'N/A') {
        total += Number(v);
        hasAny = true;
      }
    }
    return hasAny ? total : 'N/A';
  };

  // ─── Days supplied (total stock / avg monthly forecast, ~30 days/month) ──
  const computeTotalDaysSupplied = (product, avgGrowthByMp) => {
    const totalStock = getTotalStock(product);
    if (totalStock <= 0) return 0;

    let remaining = totalStock;
    let days = 0;
    const allMonths = [
      { year: currentMonthInfo.year, month: currentMonthInfo.month },
      ...forecastMonths,
    ];

    for (const { year, month } of allMonths) {
      const forecastVal = sumForecast(product, year, month, avgGrowthByMp);
      const forecastNum = forecastVal !== 'N/A' ? Number(forecastVal) : 0;
      if (forecastNum <= 0) continue;
      if (remaining >= forecastNum) {
        remaining -= forecastNum;
        days += 30;
      } else {
        days += Math.floor((remaining / forecastNum) * 30);
        break;
      }
    }
    return days;
  };

  // ─── Est. End-of-Month Stock balances ────────────────────────────────────
  // Stores forecastNum per month so calcMonthsOfStock can use it.
  const computeMonthBalances = (product, avgGrowthByMp) => {
    const sku = product.sku;
    const initialStock = getTotalStock(product);

    const monthsToCalc = [
      { year: currentMonthInfo.year, month: currentMonthInfo.month },
      ...forecastMonths,
    ];

    const balances = {};
    let prevBalance = initialStock;

    for (const { year, month } of monthsToCalc) {
      const key        = `${year}-${String(month).padStart(2, '0')}`;
      const forecastVal = sumForecast(product, year, month, avgGrowthByMp);
      const forecastNum = forecastVal !== 'N/A' ? Number(forecastVal) : 0;
      const production  = (monthlyPlanData[sku] || {})[key] || 0;

      const startBalance = Math.max(0, prevBalance);
      const endBalance   = startBalance + production - forecastNum;

      balances[key] = {
        endBalance,
        forecastNum, // needed by calcMonthsOfStock
        tooltip: `Start: ${startBalance.toLocaleString()}\n+ Production: ${production.toLocaleString()}\n− Forecast (all): ${forecastNum.toLocaleString()}\n= ${endBalance.toLocaleString()}`,
      };
      prevBalance = endBalance;
    }
    return balances;
  };

  // How many future forecast months does endBalance cover?
  const calcMonthsOfStock = (endBalance, monthBalances) => {
    if (endBalance <= 0) return 0;
    let remaining = endBalance;
    let months = 0;
    for (const fm of forecastMonths) {
      const fmKey      = `${fm.year}-${String(fm.month).padStart(2, '0')}`;
      const fmForecast = monthBalances[fmKey]?.forecastNum ?? 0;
      if (fmForecast <= 0 || remaining >= fmForecast) {
        remaining -= fmForecast > 0 ? fmForecast : 0;
        months++;
      } else {
        break;
      }
    }
    return months;
  };

  // ─── Render helpers ───────────────────────────────────────────────────────
  const fmt = (v) => {
    if (v === 'N/A' || v === null || v === undefined) return 'N/A';
    return typeof v === 'number' ? v.toLocaleString() : v;
  };

  const pctChange = (current, previous) => {
    const change = calculateMonthlyPercentageChange(current, previous);
    if (!change) return null;
    return (
      <span className={`change-indicator ${change.startsWith('+') ? 'positive' : 'negative'}`}>
        {change}
      </span>
    );
  };

  const renderBalanceCell = (entry, extraClass = '', monthBalances = {}) => {
    if (!entry) {
      return <td className={`stock-balance-cell stock-balance-empty ${extraClass}`}>—</td>;
    }
    const { endBalance, tooltip } = entry;
    const cls = endBalance < 0 ? 'balance-negative' : endBalance === 0 ? 'balance-zero' : 'balance-positive';
    const monthsOfStock = calcMonthsOfStock(endBalance, monthBalances);
    return (
      <td className={`stock-balance-cell ${extraClass} ${cls}`} title={tooltip}>
        {endBalance.toLocaleString()}
        <span className="stock-weeks-suffix" style={{ opacity: 0.65, fontSize: '0.85em', marginLeft: '3px' }}>
          /{monthsOfStock}m
        </span>
      </td>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  const currentKey = `${currentMonthInfo.year}-${String(currentMonthInfo.month).padStart(2, '0')}`;

  return (
    <div className="table-responsive summary-table-wrapper">
      <table className="yoy-table yoy-monthly-table summary-table">
        <thead>
          <tr>
            <th rowSpan="2" className="yoy-fixed-column product-name-head">Product Name</th>
            <th rowSpan="2">{identifierField.toUpperCase()}</th>
            <th rowSpan="2">Total Stock<br />(All Markets)</th>
            <th rowSpan="2">Days Supplied<br />(Total)</th>
            <th rowSpan="2">Due Date</th>
            <th rowSpan="2">Plan to<br />Produce</th>

            {visibleColumns.historical && historicalMonths.length > 0 && (
              <th colSpan={historicalMonths.length} className="section-header">
                Historical Data ({historicalMonthsCount} months)
              </th>
            )}

            {visibleColumns.currentWeek && (
              <th rowSpan="2" className="current-week-header section-header">
                Current Month ({currentMonthInfo.label} {currentMonthInfo.year})
                <div style={{ fontSize: '10px', fontWeight: 'normal' }}>
                  {currentMonthInfo.dateRange}
                </div>
              </th>
            )}

            {visibleColumns.prediction && forecastMonths.length > 0 && (
              <th colSpan={forecastMonths.length} className="section-header">
                Prediction ({forecastMonthsCount} months)
              </th>
            )}
          </tr>

          <tr>
            {visibleColumns.historical && historicalMonths.map((m, idx) => (
              <th key={idx} className="week-header">
                <div>{m.label} {m.year}</div>
                <div style={{ fontSize: '9px', fontWeight: 'normal' }}>{m.dateRange}</div>
              </th>
            ))}
            {visibleColumns.prediction && forecastMonths.map((m, idx) => (
              <th key={idx} className="prediction-header">
                {m.label} {m.year}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {filteredProducts.map(product => {
            const identifier     = product[identifierField];
            const avgGrowthByMp  = avgGrowthMap[identifier] || {};
            const totalStock     = getTotalStock(product);
            const totalDays      = computeTotalDaysSupplied(product, avgGrowthByMp) || 0;
            const monthBalances  = showStockBalanceRow
              ? computeMonthBalances(product, avgGrowthByMp)
              : null;
            const planQty = planToProduceData?.[product.sku] || 0;

            // rowSpan="2" — spans Previous Year + Current Year rows
            const fixedLeftCells = (
              <>
                <td rowSpan="2" className="yoy-fixed-column product-cell">{product.productName}</td>
                <td rowSpan="2" className="identifier-cell">{identifier}</td>
                <td rowSpan="2" className="stock-cell">{totalStock.toLocaleString()}</td>
                <td rowSpan="2" className="days-supplied-total-cell">
                  {renderDaysSuppliedCell(totalDays)}
                </td>
                <td rowSpan="2" className="due-date-cell">
                  <div className={`due-date-value ${getDueDateClass(totalDays)}`}>
                    {calculateDueDate(totalDays)}
                  </div>
                </td>
                <td rowSpan="2" className="plan-to-produce-cell">
                  {planQty > 0 ? planQty.toLocaleString() : '—'}
                </td>
              </>
            );

            return (
              <React.Fragment key={product.id}>

                {/* ── Previous Year Row ──────────────────────────────── */}
                <tr className="previous-year-row">
                  {fixedLeftCells}

                  {visibleColumns.historical && historicalMonths.map((m, idx) => (
                    <td key={idx} className="historical-cell previous-year">
                      {fmt(sumMonthlySales(product, m.year - 1, m.month))}
                    </td>
                  ))}

                  {visibleColumns.currentWeek && (
                    <td className="current-week-cell previous-year">
                      {fmt(sumMonthlySales(product, currentMonthInfo.year - 1, currentMonthInfo.month))}
                    </td>
                  )}

                  {visibleColumns.prediction && forecastMonths.map((m, idx) => (
                    <td key={idx} className="prediction-cell previous-year">
                      {fmt(sumMonthlySales(product, m.year - 1, m.month))}
                    </td>
                  ))}
                </tr>

                {/* ── Current Year Row ───────────────────────────────── */}
                <tr className="current-year-row">

                  {/* Historical — actual data */}
                  {visibleColumns.historical && historicalMonths.map((m, idx) => {
                    const cur  = sumMonthlySales(product, m.year, m.month);
                    const prev = sumMonthlySales(product, m.year - 1, m.month);
                    return (
                      <td key={idx} className="historical-cell">
                        <div className="cell-content">
                          <div className="value-with-edit"><span>{fmt(cur)}</span></div>
                          {pctChange(cur, prev)}
                        </div>
                      </td>
                    );
                  })}

                  {/* Current month — summed forecast */}
                  {visibleColumns.currentWeek && (
                    <td className="current-week-cell">
                      <div className="value-with-edit">
                        {fmt(sumForecast(product, currentMonthInfo.year, currentMonthInfo.month, avgGrowthByMp))}
                      </div>
                    </td>
                  )}

                  {/* Forecast months */}
                  {visibleColumns.prediction && forecastMonths.map((m, idx) => {
                    const forecast = sumForecast(product, m.year, m.month, avgGrowthByMp);
                    const previous = sumMonthlySales(product, m.year - 1, m.month);
                    return (
                      <td key={idx} className="prediction-cell">
                        <div className="current-year-value">
                          {fmt(forecast)}
                          {pctChange(forecast, previous)}
                        </div>
                      </td>
                    );
                  })}
                </tr>

                {/* ── Est. End-of-Month Stock Row ─────────────────────── */}
                {showStockBalanceRow && monthBalances && (
                  <tr className="stock-balance-row">
                    <td colSpan={FIXED_COL_COUNT} className="stock-balance-label-cell">
                      <span className="stock-balance-label">Est. End-of-Month Stock</span>
                    </td>

                    {/* Historical — empty cells for alignment */}
                    {visibleColumns.historical && historicalMonths.map((_, idx) => (
                      <td key={idx} className="stock-balance-cell stock-balance-empty" />
                    ))}

                    {/* Current month balance */}
                    {visibleColumns.currentWeek && renderBalanceCell(
                      monthBalances[currentKey], 'current-week-cell', monthBalances
                    )}

                    {/* Forecast month balances */}
                    {visibleColumns.prediction && forecastMonths.map((m, idx) => {
                      const key = `${m.year}-${String(m.month).padStart(2, '0')}`;
                      return React.cloneElement(
                        renderBalanceCell(monthBalances[key], 'prediction-cell', monthBalances),
                        { key: idx }
                      );
                    })}
                  </tr>
                )}

              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default YoySummaryMonthlyTable;